import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { inventoryApi } from '#services/inventory';
import {
  DEFAULT_TIME_RANGE_PRESET,
  TimeRangeValue,
  buildTimeRange,
} from '#components/time-range.utils';
import { revertAll } from '../actions';

interface InventoryOrderListState {
  filter: string;
  dateRange: TimeRangeValue;
  filterKey: string;
  headerFilters: Record<string, Record<string, boolean>>;
  order: string;
  orderBy: string;
  pageIndex: number;
  pageSize: number;
  rows: unknown[];
  rowCount: number;
  selected: Array<string | number | undefined>;
  vendors: Record<string, unknown>;
  skip: boolean;
}

const initialState: InventoryOrderListState = {
  filter: '',
  dateRange: buildTimeRange(DEFAULT_TIME_RANGE_PRESET),
  filterKey: 'inventoryOrderId',
  headerFilters: {},
  order: 'desc',
  orderBy: 'id',
  pageIndex: 0,
  pageSize: 30,
  rows: [],
  rowCount: 0,
  selected: [],
  vendors: {},
  skip: false,
};

export const inventoryOrderListSlice = createSlice({
  name: 'inventoryList',
  initialState,
  reducers: {
    setDateRange(state, action: PayloadAction<TimeRangeValue>) {
      state.dateRange = action.payload;
      state.pageIndex = 0;
      state.skip = false;
    },
    setFilterKey(state, action: PayloadAction<string>) {
      state.filterKey = action.payload;
      state.skip = true;
    },
    setFilter(state, action: PayloadAction<string>) {
      state.filter = action.payload;
      state.pageIndex = 0;
      state.skip = false;
    },
    setHeaderFilters(state, action: PayloadAction<Record<string, Record<string, boolean>>>) {
      state.headerFilters = action.payload;
      state.skip = false;
    },
    setOrder(state, action: PayloadAction<string>) {
      state.order = action.payload;
      state.skip = false;
    },
    setOrderBy(state, action: PayloadAction<string>) {
      state.orderBy = action.payload;
      state.skip = false;
    },
    setPageIndex(state, action: PayloadAction<number>) {
      state.pageIndex = action.payload;
      state.skip = false;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      state.pageIndex = 0;
      state.skip = false;
    },
    setRows(state, action: PayloadAction<unknown[]>) {
      state.rows = action.payload;
    },
    setRowCount(state, action: PayloadAction<number>) {
      state.rowCount = action.payload;
    },
    setSelected(state, action: PayloadAction<Array<string | number | undefined>>) {
      const { payload } = action;
      state.selected = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(revertAll, () => initialState)
      .addMatcher(inventoryApi.endpoints.getInventoryOrders.matchFulfilled, (state, action) => {
        const {
          page,
          rows,
          total,
          vendors,
        } = action.payload as {
          page: number;
          rows: unknown[];
          total: number;
          vendors: Record<string, unknown>;
        };
        state.rows = rows;
        state.rowCount = total;
        state.pageIndex = page;
        state.vendors = vendors;
        // dont clear the selected data,
        // the selected states should be remembered for the crossing page selection
        // state.selected = [];
      });
  },
});

export const {
  setDateRange,
  setFilterKey,
  setFilter,
  setHeaderFilters,
  setOrder,
  setOrderBy,
  setPageIndex,
  setPageSize,
  setRows,
  setRowCount,
  setSelected,
} = inventoryOrderListSlice.actions;

export default inventoryOrderListSlice.reducer;
